"use strict";
// Copyright © Veeam Software Group GmbH
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Modal = void 0;
const react_1 = __importStar(require("react"));
const reflect_1 = require("../helpers/reflect");
const Modality_1 = require("./components/Modality/Modality");
const ModalRoot_1 = require("./components/ModalRoot/ModalRoot");
const SidePanelWrapper_1 = require("./components/SidePanelWrapper/SidePanelWrapper");
const ModalContext_1 = require("./context/ModalContext");
const Overlay_1 = require("../Overlay");
const PageViewport_1 = require("./components/PageViewport/PageViewport");
const ModalPopoverContext_1 = require("./context/ModalPopoverContext");
const types_1 = require("./types");
exports.Modal = (0, react_1.memo)((_a) => {
    var { render: renderProp, children, isShown, onRequestClose, view = types_1.MODAL_VIEW.dialog, overlayColor, containerRef, hasSidePanelAnimation, onContainerClick } = _a, rest = __rest(_a, ["render", "children", "isShown", "onRequestClose", "view", "overlayColor", "containerRef", "hasSidePanelAnimation", "onContainerClick"]);
    const [topOffset, setTopOffset] = (0, react_1.useState)(undefined);
    const elementRef = (0, react_1.useRef)(null);
    const { addToStack, removeFromStack, notify, addListener, getLastFromStack } = (0, react_1.useContext)(ModalContext_1.ModalContext);
    const removeListenerRef = (0, react_1.useRef)(() => { });
    const childPopoverElementsRef = (0, react_1.useRef)([]);
    const isTopModalRef = (0, react_1.useRef)(true);
    const render = (0, react_1.useMemo)(() => renderProp || children, [children, renderProp]);
    (0, react_1.useEffect)(() => {
        return addListener(() => {
            isTopModalRef.current = getLastFromStack() === elementRef.current;
        });
    }, [addListener, getLastFromStack]);
    (0, react_1.useEffect)(() => {
        // Prevent focus outside the modal
        function handler(e) {
            var _a, _b;
            if (!(0, reflect_1.isHTMLElement)(e.target) || !(0, reflect_1.isHTMLElement)(elementRef.current)) {
                return;
            }
            if (elementRef.current.contains(e.target) ||
                childPopoverElementsRef.current.some((el) => el.contains(e.target))) {
                return;
            }
            if (isTopModalRef.current) {
                e.preventDefault();
                e.stopPropagation();
                e.target.blur();
                (_b = (_a = elementRef.current.firstChild) === null || _a === void 0 ? void 0 : _a.focus) === null || _b === void 0 ? void 0 : _b.call(_a);
            }
        }
        if (!(containerRef === null || containerRef === void 0 ? void 0 : containerRef.current)) {
            document.body.addEventListener('focusin', handler);
            return () => {
                document.body.removeEventListener('focusin', handler);
            };
        }
        return;
    }, [containerRef]);
    (0, react_1.useEffect)(() => {
        if (containerRef === null || containerRef === void 0 ? void 0 : containerRef.current) {
            setTopOffset(containerRef.current.scrollTop);
        }
    }, [containerRef]);
    const isDialog = (0, react_1.useMemo)(() => view === types_1.MODAL_VIEW.dialog, [view]);
    const isSidePanel = (0, react_1.useMemo)(() => view === types_1.MODAL_VIEW.sidePanel, [view]);
    const isPageView = (0, react_1.useMemo)(() => !isDialog && !isSidePanel, [isDialog, isSidePanel]);
    const handleMount = (0, react_1.useCallback)(() => {
        const el = elementRef.current;
        if (!el || !(0, reflect_1.isHTMLElement)(el)) {
            return;
        }
        addToStack(el);
        removeListenerRef.current = addListener(() => {
            setTimeout(() => {
                var _a, _b, _c;
                if ((_a = getLastFromStack()) === null || _a === void 0 ? void 0 : _a.contains(el)) {
                    (_c = (_b = el === null || el === void 0 ? void 0 : el.firstChild) === null || _b === void 0 ? void 0 : _b.focus) === null || _c === void 0 ? void 0 : _c.call(_b);
                }
            }, 0);
        });
        notify();
    }, [addListener, addToStack, getLastFromStack, notify]);
    const handleUnmount = (0, react_1.useCallback)(() => {
        if (elementRef.current) {
            removeFromStack(elementRef.current);
            removeListenerRef.current();
            notify();
        }
    }, [notify, removeFromStack]);
    const outsideClickHandler = (0, react_1.useCallback)((e) => {
        if (elementRef.current === e.target) {
            onContainerClick === null || onContainerClick === void 0 ? void 0 : onContainerClick();
        }
    }, [onContainerClick]);
    (0, react_1.useEffect)(() => {
        if (onContainerClick) {
            const container = (containerRef === null || containerRef === void 0 ? void 0 : containerRef.current) || document;
            container.addEventListener('click', outsideClickHandler, { capture: true });
            return () => {
                container.removeEventListener('click', outsideClickHandler, { capture: true });
            };
        }
        return;
    }, [onContainerClick, outsideClickHandler, containerRef]);
    if (!render) {
        return null;
    }
    return (react_1.default.createElement(Modality_1.Modality, { isShown: isShown, onRequestClose: onRequestClose, containerRef: containerRef, render: (injectProps) => {
            return (react_1.default.createElement(ModalPopoverContext_1.ModalPopoverContext.Provider, { value: {
                    addChild(el) {
                        childPopoverElementsRef.current.push(el);
                        return () => {
                            childPopoverElementsRef.current =
                                childPopoverElementsRef.current.filter((i) => i !== el);
                        };
                    }
                } },
                react_1.default.createElement(ModalRoot_1.ModalRoot, { onMount: handleMount, onUnmount: handleUnmount },
                    react_1.default.createElement(react_1.default.Fragment, null,
                        isDialog && (react_1.default.createElement(Overlay_1.Overlay, { fixed: !containerRef, view: Overlay_1.OVERLAY_VIEW.tone, contentMod: Overlay_1.OVERLAY_CONTENT_MOD.center, ref: elementRef, topOffset: topOffset, "data-selector-id": rest['data-selector-id'] || 'modal-overlay', color: overlayColor }, render(injectProps) || null)),
                        isSidePanel && (react_1.default.createElement(Overlay_1.Overlay, { fixed: !containerRef, view: Overlay_1.OVERLAY_VIEW.tone, contentMod: Overlay_1.OVERLAY_CONTENT_MOD.right, ref: elementRef, topOffset: topOffset, "data-selector-id": rest['data-selector-id'] ||
                                'modal-side-panel-overlay', color: overlayColor },
                            react_1.default.createElement(SidePanelWrapper_1.SidePanelWrapper, { hasAnimation: hasSidePanelAnimation }, render(injectProps) || null))),
                        isPageView && (react_1.default.createElement(PageViewport_1.PageViewPort, { color: overlayColor, isFullPage: view === types_1.MODAL_VIEW.fullPage, "data-selector-id": rest['data-selector-id'] || 'modal-page', ref: elementRef }, render(injectProps) || null))))));
        } }));
});
exports.Modal.displayName = 'Modal';
//# sourceMappingURL=Modal.js.map